"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { iSupplierPaymentMethod } from "@sdk/supplier-payment-methods/supplier-payment-method-model";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { useBrands } from "@/hooks/use-brands";
import { CurrencySelector } from "@/components/currency-selector";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { LedgerSelector } from "@/components/ledgers/ledger-selector";
import {
  QuickBooksAccountSelector,
  formatQuickBooksAccountLabel,
} from "@/components/integrations/quickbooks-account-selector";
import { useQuickBooksAccounts } from "@/components/integrations/use-quickbooks-accounts";
import { SupplierPaymentMethodDefaultLedgerLinks } from "@/components/accounting/default-ledger-links";
import { PaymentMethodBrandRestrictionFields } from "@/components/payment-methods/payment-method-brand-restriction-fields";

export interface AddEditSupplierPaymentMethodModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  paymentMethod?: iSupplierPaymentMethod;
  onSuccess?: () => void;
  retry: () => void;
}

export function AddEditSupplierPaymentMethodModal({
  open,
  onOpenChange,
  mode,
  paymentMethod,
  onSuccess,
  retry,
}: AddEditSupplierPaymentMethodModalProps) {
  const [label, setLabel] = useState(paymentMethod?.label ?? "");
  const [connectedPaymentAccountLedger, setConnectedPaymentAccountLedger] =
    useState(paymentMethod?.connectedPaymentAccountLedger ?? "");
  const [connectedQuickBooksAccountId, setConnectedQuickBooksAccountId] =
    useState(paymentMethod?.connectedQuickBooksAccountId ?? "");
  const [connectedQuickBooksAccountName, setConnectedQuickBooksAccountName] =
    useState(paymentMethod?.connectedQuickBooksAccountName ?? "");
  const [connectedQuickBooksAccountType, setConnectedQuickBooksAccountType] =
    useState(paymentMethod?.connectedQuickBooksAccountType ?? "");
  const [archived, setArchived] = useState(paymentMethod?.archived ?? false);
  const [restrictByBrands, setRestrictByBrands] = useState(
    paymentMethod?.restrictByBrands ?? false
  );
  const [allowedBrands, setAllowedBrands] = useState<string[]>(
    paymentMethod?.allowedBrands ?? []
  );
  const { brands } = useBrands();
  const defaultBrandCurrency = useMemo(() => {
    const defaultBrand = brands.find((brand) => brand.isDefault) ?? brands[0];
    return defaultBrand?.homeCurrency?.trim().toUpperCase() ?? "GBP";
  }, [brands]);
  const [defaultCurrency, setDefaultCurrency] = useState(
    paymentMethod?.defaultCurrency?.trim().toUpperCase() ?? defaultBrandCurrency
  );

  const { connected: quickBooksConnected, accounts: quickBooksAccounts } =
    useQuickBooksAccounts(open);

  useEffect(() => {
    if (open) {
      setLabel(paymentMethod?.label ?? "");
      setConnectedPaymentAccountLedger(
        paymentMethod?.connectedPaymentAccountLedger ?? ""
      );
      setConnectedQuickBooksAccountId(
        paymentMethod?.connectedQuickBooksAccountId ?? ""
      );
      setConnectedQuickBooksAccountName(
        paymentMethod?.connectedQuickBooksAccountName ?? ""
      );
      setConnectedQuickBooksAccountType(
        paymentMethod?.connectedQuickBooksAccountType ?? ""
      );
      setArchived(paymentMethod?.archived ?? false);
      setRestrictByBrands(paymentMethod?.restrictByBrands ?? false);
      setAllowedBrands(paymentMethod?.allowedBrands ?? []);
      setDefaultCurrency(
        paymentMethod?.defaultCurrency?.trim().toUpperCase() ??
          defaultBrandCurrency
      );
    }
  }, [
    open,
    paymentMethod?.label,
    paymentMethod?.connectedPaymentAccountLedger,
    paymentMethod?.connectedQuickBooksAccountId,
    paymentMethod?.connectedQuickBooksAccountName,
    paymentMethod?.connectedQuickBooksAccountType,
    paymentMethod?.archived,
    paymentMethod?.restrictByBrands,
    paymentMethod?.allowedBrands,
    paymentMethod?.defaultCurrency,
    defaultBrandCurrency,
  ]);

  const { doAction: createPaymentMethod, isProcessing: isCreateProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (details: Partial<iSupplierPaymentMethod>) =>
          SDK.supplierPaymentMethods.create(details),
        successMessage: "Payment method has been created",
        failureMessage: "Something went wrong",
      }),
      []
    );

  const { doAction: editPaymentMethod, isProcessing: isEditProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (
          id: string,
          edits: Partial<iSupplierPaymentMethod>
        ) => SDK.supplierPaymentMethods.editById(id, edits),
        successMessage: "Payment method has been updated",
        failureMessage: "Something went wrong",
      }),
      []
    );

  const isProcessing = isCreateProcessing || isEditProcessing;
  const title =
    mode === "create" ? "Add payment method" : "Edit payment method";
  const connectedLedgerCurrency =
    mode === "create"
      ? defaultCurrency
      : paymentMethod?.defaultCurrency?.trim().toUpperCase() ?? defaultCurrency;

  const handleSubmit = useCallback(async () => {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) return;

    const currency = defaultCurrency.trim().toUpperCase();
    if (mode === "create" && !currency) return;

    const quickBooksFields = {
      connectedQuickBooksAccountId:
        connectedQuickBooksAccountId?.trim() || undefined,
      connectedQuickBooksAccountName:
        connectedQuickBooksAccountId?.trim()
          ? connectedQuickBooksAccountName?.trim() || undefined
          : undefined,
      connectedQuickBooksAccountType:
        connectedQuickBooksAccountId?.trim()
          ? connectedQuickBooksAccountType?.trim() || undefined
          : undefined,
    };

    const brandRestrictionFields = {
      restrictByBrands,
      allowedBrands: restrictByBrands ? allowedBrands : undefined,
    };

    if (mode === "create") {
      const created = await createPaymentMethod({
        label: trimmedLabel,
        type: "DEFAULT",
        defaultCurrency: currency,
        connectedPaymentAccountLedger:
          connectedPaymentAccountLedger?.trim() || undefined,
        ...quickBooksFields,
        ...brandRestrictionFields,
      });
      if (created) {
        retry();
        onOpenChange(false);
        onSuccess?.();
      }
    } else if (paymentMethod?.id) {
      await editPaymentMethod(paymentMethod.id, {
        label: trimmedLabel,
        connectedPaymentAccountLedger:
          connectedPaymentAccountLedger?.trim() || undefined,
        ...quickBooksFields,
        ...brandRestrictionFields,
        archived,
      });
      retry();
      onOpenChange(false);
      onSuccess?.();
    }
  }, [
    label,
    defaultCurrency,
    mode,
    paymentMethod?.id,
    connectedPaymentAccountLedger,
    connectedQuickBooksAccountId,
    connectedQuickBooksAccountName,
    connectedQuickBooksAccountType,
    restrictByBrands,
    allowedBrands,
    archived,
    createPaymentMethod,
    editPaymentMethod,
    retry,
    onOpenChange,
    onSuccess,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="supplier-pm-label">Label</Label>
            <Input
              id="supplier-pm-label"
              placeholder="e.g. Card, Credit terms"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          </div>

          {mode === "create" ? (
            <CurrencySelector
              id="supplier-pm-default-currency"
              label="Default Accounting Currency"
              value={defaultCurrency}
              onValueChange={setDefaultCurrency}
              extraCurrencies={[defaultBrandCurrency]}
            />
          ) : paymentMethod?.defaultCurrency ? (
            <div className="grid gap-2">
              <Label>Default Accounting Currency</Label>
              <Input
                value={paymentMethod.defaultCurrency}
                readOnly
                disabled
                className="bg-muted"
              />
            </div>
          ) : null}

          {mode === "create" ? (
            <p className="text-xs text-muted-foreground -mt-2">
              The clearing ledger is created in this currency. It cannot be
              changed later.
            </p>
          ) : null}

          <div className="space-y-2">
            <LedgerSelector
              label="Connected ledger account"
              value={connectedPaymentAccountLedger}
              onValueChange={setConnectedPaymentAccountLedger}
              allowNone
              paymentAccountLedgersOnly
              currencyFilter={connectedLedgerCurrency}
              placeholder="Select ledger account"
            />
            <p className="text-xs text-muted-foreground">
              When set, supplier payment receipts using this method will
              auto-post a ledger transaction. The ledger must be in{" "}
              {connectedLedgerCurrency || "the payment method currency"}.
            </p>
          </div>

          <PaymentMethodBrandRestrictionFields
            restrictByBrands={restrictByBrands}
            allowedBrands={allowedBrands}
            onRestrictByBrandsChange={setRestrictByBrands}
            onAllowedBrandsChange={setAllowedBrands}
          />

          {quickBooksConnected ? (
            <div className="space-y-2 rounded-lg border p-3">
              <QuickBooksAccountSelector
                id="supplier-pm-qb-account"
                label="QuickBooks bank account"
                accounts={quickBooksAccounts}
                value={connectedQuickBooksAccountId}
                onValueChange={(accountId, account) => {
                  setConnectedQuickBooksAccountId(accountId);
                  setConnectedQuickBooksAccountName(
                    account?.Name ?? account?.name ?? ""
                  );
                  setConnectedQuickBooksAccountType(
                    account?.AccountType ?? account?.accountType ?? ""
                  );
                }}
                accountTypeFilter={["Bank"]}
                allowNone
                noneLabel="No QuickBooks account"
                placeholder="Select bank account"
              />
              <p className="text-xs text-muted-foreground">
                Required for QuickBooks bill payment sync. Supplier payments are paid from this account.
                {connectedQuickBooksAccountId ? (
                  <>
                    {" "}
                    Selected:{" "}
                    <span className="font-medium">
                      {formatQuickBooksAccountLabel({
                        id: connectedQuickBooksAccountId,
                        name: connectedQuickBooksAccountName,
                        accountType: connectedQuickBooksAccountType,
                      })}
                    </span>
                  </>
                ) : null}
              </p>
            </div>
          ) : null}

          {mode === "edit" && paymentMethod?.defaultLedgerAccounts ? (
            <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
              <Label className="text-sm">System accounting ledgers</Label>
              <p className="text-xs text-muted-foreground">
                Auto-provisioned when this payment method was created. Used for
                journal posting on supplier payments.
              </p>
              <SupplierPaymentMethodDefaultLedgerLinks
                defaultLedgerAccounts={paymentMethod.defaultLedgerAccounts}
              />
            </div>
          ) : null}

          {mode === "edit" ? (
            <div className="flex items-center gap-2 rounded-lg border p-3">
              <Checkbox
                id="supplier-pm-archived"
                checked={archived}
                onCheckedChange={(checked) => setArchived(checked === true)}
              />
              <div className="space-y-1">
                <Label htmlFor="supplier-pm-archived" className="text-sm">
                  Archived
                </Label>
                <p className="text-xs text-muted-foreground">
                  Archived methods are hidden from new receipts but remain on
                  existing records.
                </p>
              </div>
            </div>
          ) : null}
        </div>
        </div>
        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isProcessing || !label.trim() || (mode === "create" && !defaultCurrency.trim())}
          >
            {isProcessing ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
